import { queryClient } from '$/services/client';
import { backendEndpoints } from '$/services/endpoints';
import { fetcher } from '$/services/fetcher';
import { GeneralBackendResponseObjectSchema } from '$/services/mapper/authentication';
import { validateToken } from '$/services/usecases/authentication';
import { studentInvitationsQuery } from '$/services/usecases/invitation/queries';
import {
  AcceptStudentInvitationResponseSchema,
  GetAllStudentsResponseSchema,
} from '$/services/usecases/school/mapper/students';

export const getAllStudents = async (signal: AbortSignal) => {
  await validateToken();
  return fetcher(backendEndpoints.SCHOOL_STUDENTS_URL, {
    method: 'GET',
    includeCredentials: true,
    parser: (data) => GetAllStudentsResponseSchema.parse(data).payload,
    signal,
  });
};

export const inviteStudent = async (payload: {
  email: string;
  firstName: string;
  lastName: string;
  schoolClassId: string;
  expirationDate?: string;
}) => {
  const { email, firstName, lastName, schoolClassId, expirationDate } = payload;
  await validateToken();

  const response = await fetcher(
    `${backendEndpoints.SCHOOL_STUDENTS_URL}/invite`,
    {
      method: 'POST',
      body: {
        email,
        firstName,
        lastName,
        schoolClassId,
        expirationDate,
      },
      includeCredentials: true,
      parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
    },
  );

  if (response.isSuccessful) {
    await queryClient.invalidateQueries({
      queryKey: studentInvitationsQuery.queryKey,
    });
  }

  return response;
};

export const acceptStudentInvitation = async (payload: {
  id: string;
  password?: string;
}) => {
  return fetcher(
    `${backendEndpoints.SCHOOL_STUDENTS_URL}/acceptInvitation/${payload.id}`,
    {
      method: 'POST',
      body: {
        password: payload.password,
      },
      includeCredentials: true,
      parser: (data) => AcceptStudentInvitationResponseSchema.parse(data),
    },
  );
};

export const editStudent = async (payload: {
  studentId: string;
  expirationDate?: string;
  schoolClassId?: string;
}) => {
  await validateToken();
  const { studentId, ...body } = payload;
  const response = await fetcher(
    `${backendEndpoints.SCHOOL_STUDENTS_URL}/${studentId}`,
    {
      method: 'PUT',
      body,
      includeCredentials: true,
      parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
    },
  );

  if (response.isSuccessful) {
    await queryClient.invalidateQueries({ queryKey: ['students'] });
  }

  return response;
};

export const deleteStudent = async (studentId: string) => {
  await validateToken();
  const response = await fetcher(
    `${backendEndpoints.SCHOOL_STUDENTS_URL}/${studentId}`,
    {
      method: 'DELETE',
      includeCredentials: true,
      parser: (data) => GeneralBackendResponseObjectSchema.parse(data),
    },
  );

  if (response.isSuccessful) {
    await queryClient.invalidateQueries({ queryKey: ['students'] });
  }

  return response;
};
